import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import axios from 'axios';
import availablePermissions from '@/constants/temp-permissions.json';
import helpers from '@/helpers/helpers';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import api from '@/lib/Api';

const MASTER_PASSWORD_REENTRY_AFTER_CREATE = 'master_password_reentry_after_create';

function readMasterPasswordReentryAfterCreate() {
  try {
    return sessionStorage.getItem(MASTER_PASSWORD_REENTRY_AFTER_CREATE) === '1';
  } catch {
    return false;
  }
}

export const useUserStore = defineStore('user', () => {
  const showEmailModal = ref(false);
  const masterPasswordReentryAfterCreate = ref(readMasterPasswordReentryAfterCreate());
  const fetchingUser = ref(false);
  /** True while router beforeEach auth/bootstrap work is in progress */
  const authBootstrapping = ref(false);
  const user = ref(null);
  const hasKey = ref(false);
  const key = ref(null);
  const email = ref(null);
  const name = ref(null);
  const client_contact_email = ref(null);
  const client_contact_name = ref(null);
  const initialized = ref(false);
  const activeClient = computed(() => user.value?.active_client_id);
  const currentClient = computed(() => user.value?.client);
  const activeClientAbility = computed(() => (user.value?.abilities?.length ? user.value.abilities[0] : null));
  const userAccess = ref({ permissions: [], roles: [], subscriptions: [] });
  const isAdmin = computed(() => user.value?.is_admin);
  const isClientContact = computed(() => userAccess.value.roles.includes('client_contact'));
  const mainAbility = computed(() => (isAdmin.value ? 'admin' : activeClientAbility.value));
  const loggedIn = computed(() => user.value?.id > 0);
  const clients = computed(() =>
    user.value?.clients.map((client) => ({
      id: client?.id,
      name: client?.name,
      abilities: client?.abilities,
    })),
  );
  const isDelegatedUser = computed(() => userAccess.value?.roles[0] === 'delegated_user');
  const router = useRouter();
  const { locale } = useI18n();
  const increaseLoginTime = ref(false);
  const unsecureLogin = ref(false);
  const passwordlessLogin = ref(false);
  const statements = ref({ data_room: [], insider_list: [], advisory_list: [] });

  function syncUnsecureLoginFromUser() {
    unsecureLogin.value = !!(user.value?.client?.unsecure_login ?? false);
  }

  function syncPasswordlessLoginFromUser() {
    passwordlessLogin.value = !!(user.value?.client?.passwordless_login ?? false);
  }

  async function updateIncreaseLoginTime(value) {
    try {
      const { data } = await axios.post('/api/auth/me/increase-login-time/toggle', {
        password: key.value,
      });
      const next = typeof data?.increase_login_time === 'boolean' ? data.increase_login_time : value;
      increaseLoginTime.value = next;
      if (user.value) {
        user.value.increase_login_time = next;
      }
    } catch (err) {
      helpers.displayAllErrorsAsToasts(err);
    }
  }

  async function toggleUnsecureLogin(value) {
    const clientId = activeClient.value;
    if (!clientId) return;
    try {
      const { data } = await axios.post(`/api/client/${clientId}/toggle-unsecure-login`);
      const next = typeof data?.unsecure_login === 'boolean' ? data.unsecure_login : value;
      unsecureLogin.value = next;
      if (user.value?.client) {
        user.value.client.unsecure_login = next;
      }
    } catch (err) {
      helpers.displayAllErrorsAsToasts(err);
    }
  }

  async function togglePasswordlessLogin(value) {
    const clientId = activeClient.value;
    if (!clientId) return;
    try {
      const { data } = await axios.post(`/api/client/${clientId}/toggle-passwordless-login`);
      const next = typeof data?.passwordless_login === 'boolean' ? data.passwordless_login : value;
      passwordlessLogin.value = next;
      if (user.value?.client) {
        user.value.client.passwordless_login = next;
      }
    } catch (err) {
      helpers.displayAllErrorsAsToasts(err);
    }
  }

  function requiresMasterKeyReentry(data) {
    const flag = data?.must_enter_master_key;
    return flag === true || flag === 1 || flag === '1';
  }

  async function fetchUser() {
    try {
      const { data } = await axios.get('/api/user/me');
      if (data?.id > 0) {
        if (requiresMasterKeyReentry(data)) {
          await removeMasterKey();
        }
        user.value = data;
        hasKey.value = data.has_key;
        user.value.permissions = availablePermissions.permissions;
        increaseLoginTime.value = !!data.increase_login_time;
        syncUnsecureLoginFromUser();
        syncPasswordlessLoginFromUser();
      }
    } catch (err) {
      /* console.log(err);*/
    }
  }

  async function fetchPendingShareRequests() {
    try {
      await axios.post('/api/util/pending-share-requests', { password: key.value });
    } catch (err) {
      /* console.log(err);*/
    }
  }

  async function requestClientDecryption() {
    try {
      const { data } = await axios.post('/api/client/share-with-me', {
        password: key.value,
        modelId: user.value?.active_client_id,
      });
      await requestClientUserDecryption();
    } catch (err) {
      /* console.log(err);*/
    }
  }

  async function requestClientUserDecryption() {
    try {
      const { data2 } = await axios.post('/api/share-request/client-user', {
        password: key.value,
        modelId: user.value?.client_user_id,
      });
    } catch (err) {
      /* console.log(err);*/
    }
  }

  async function fetchRolesAndPermissions() {
    try {
      const { data } = await axios.post('/api/auth/me/permissions', { password: key.value });
      userAccess.value = data;
    } catch (err) {
      /* console.log(err);*/
    }
  }

  async function fetchDecryptedUser() {
    try {
      fetchingUser.value = true;

      let masterKey = key.value;
      if (!masterKey) masterKey = localStorage.getItem('master_key') || null;
      key.value = masterKey;
      if (!masterKey) {
        fetchingUser.value = false;
        return;
      }
      let emailAddress = email.value;
      if (!emailAddress) {
        emailAddress = localStorage.getItem('email');
      }
      if (
        !emailAddress ||
        emailAddress.length === 0 ||
        !emailAddress.includes('@') ||
        emailAddress === 'undefined' ||
        emailAddress === null ||
        emailAddress === 'null'
      ) {
        showEmailModal.value = true;
        fetchingUser.value = false;
        return;
      }
      const { data } = await axios.post('/api/auth/me', { password: masterKey, email: emailAddress });
      // if (!emailAddress) {
      emailAddress = data.email.includes('@') ? data.email : emailAddress;
      localStorage.setItem('email', emailAddress);
      // }
      email.value = emailAddress;
      name.value = data.name;

      if (data?.id > 0) {
        user.value = data;
        client_contact_email.value = data.client_contact_email;
        client_contact_name.value = locale.value === 'hu' ? data.client_contact_name_hu : data.client_contact_name;
        user.value.permissions = availablePermissions.permissions;
        user.value.decrypted = true;
        increaseLoginTime.value = !!data.increase_login_time;
        syncUnsecureLoginFromUser();
        syncPasswordlessLoginFromUser();
      }
      fetchingUser.value = false;
      // Warm insider-list user infos without blocking navigation / auth UI
      fetchInsiderListUserInfosInBackground({ password: masterKey, email: emailAddress });
    } catch (err) {
      /* console.log(err);*/
      fetchingUser.value = false;
    }
  }

  const insiderListUserInfos = ref(null);
  let insiderListUserInfosPromise = null;

  /**
   * Fire-and-forget: concurrent callers share one in-flight request; FE never awaits this
   * from the auth/router path.
   */
  function fetchInsiderListUserInfosInBackground({ password, email: emailAddress } = {}) {
    const masterKey = password || key.value || localStorage.getItem('master_key') || null;
    const mail = emailAddress || email.value || localStorage.getItem('email') || null;
    if (!masterKey) return;

    if (insiderListUserInfosPromise) return insiderListUserInfosPromise;

    insiderListUserInfosPromise = axios
      .post('/api/auth/me/insider-list-user-infos', { password: masterKey, email: mail })
      .then(({ data }) => {
        insiderListUserInfos.value = data;
        return data;
      })
      .catch(() => null)
      .finally(() => {
        insiderListUserInfosPromise = null;
      });

    return insiderListUserInfosPromise;
  }

  function setMasterPasswordReentryAfterCreate(value) {
    masterPasswordReentryAfterCreate.value = value;
    try {
      if (value) sessionStorage.setItem(MASTER_PASSWORD_REENTRY_AFTER_CREATE, '1');
      else sessionStorage.removeItem(MASTER_PASSWORD_REENTRY_AFTER_CREATE);
    } catch {
      /* ignore */
    }
  }

  function clearMasterPasswordReentryAfterCreate() {
    setMasterPasswordReentryAfterCreate(false);
  }

  async function logout() {
    try {
      await axios.post('/api/auth/logout');
      return true;
    } catch (err) {
      /* console.log(err);*/
      return false;
    } finally {
      clearMasterPasswordReentryAfterCreate();
      user.value = null;
      increaseLoginTime.value = false;
      unsecureLogin.value = false;
      passwordlessLogin.value = false;
      setInitialized(false);

      localStorage.removeItem('access_token');
      axios.defaults.headers.common['Authorization'] = '';

      localStorage.removeItem('master_key');
      key.value = null;
      localStorage.removeItem('email');
      email.value = null;
      insiderListUserInfos.value = null;
      insiderListUserInfosPromise = null;
      await router.replace({ name: 'login' });
    }
  }

  function setInitialized(value) {
    initialized.value = value;
  }

  function setAuthBootstrapping(value) {
    authBootstrapping.value = !!value;
  }

  function setUser(value) {
    user.value = value;
  }

  async function testMasterPassword(value) {
    await axios.post('/api/keys/test', { password: value });
    key.value = value;
  }

  async function createMasterKey(payload) {
    try {
      localStorage.removeItem('master_key');
      await axios.post('/api/keys/create', {...payload, locale: locale.value });
      key.value = payload.password;
      setMasterPasswordReentryAfterCreate(true);
      helpers.successToast('Master password is created successfully');
      return true;
    } catch (err) {
      helpers.displayAllErrorsAsToasts(err);
      return false;
    }
  }

  async function editMasterKey(payload) {
    try {
      await axios.put('/api/keys/update-password', payload);
      key.value = payload.password;
      helpers.successToast('Master password is updated successfully');
      localStorage.setItem('master_key', payload.password);
      return true;
    } catch (err) {
      helpers.displayAllErrorsAsToasts(err);
      return false;
    }
  }

  async function switchUser(id) {
    await axios.post('/api/user/switch-client', { client_id: id, password: key.value });
  }

  async function updateUserInfo(payload) {
    await axios.post('/api/user/update/me', { password: key.value, ...payload });
  }

  async function getActiveClientLastChangeDate() {
    const { data } = await axios.post('/api/client/' + activeClient.value, { password: key.value });
    return data.last_change_date;
  }
  async function getRepresentativesAndBoardMembers() {
    const { data } = await axios.post(
      '/api/client/' + activeClient.value + '/representatives-and-supervising-board-members',
      { search: user.value.client.tax_number, password: key.value },
    );
    return data;
  }
  async function fetchStatements() {
    if (!user.value) return { data_room: [], insider_list: [], advisory_list: [] };
    try {
      const { data } = await axios.post('/api/user/statement-statuses', {
        password: key.value,
        userIds: [user.value.id],
      });
      const stmts = data.statements || {};
      statements.value = {
        data_room: stmts.data_room || [],
        insider_list: stmts.insider_list || [],
        advisory_list: stmts.advisory_list || [],
      };
      return statements.value;
    } catch (e) {
      return { data_room: [], insider_list: [], advisory_list: [] };
    }
  }

  async function isThisMyEmail(emailAddress) {
    let masterKey = key.value;
    if (!masterKey) masterKey = localStorage.getItem('master_key') || null;

    if (!masterKey) return false;
    const { data } = await axios.post('/api/user/is-this-my-email', { password: masterKey, email: emailAddress });
    return data.is_this_my_email;
  }

  async function removeMasterKey() {
    key.value = null;
    localStorage.removeItem('master_key');
  }

  async function permanentDeleteUser(userId) {
    const { data } = await axios.post('/api/user/delete/' + userId, { password: key.value });
    return data.message;
  }

  return {
    fetchingUser,
    authBootstrapping,
    user,
    masterPasswordReentryAfterCreate,
    initialized,
    activeClient,
    activeClientAbility,
    userAccess,
    isAdmin,
    mainAbility,
    loggedIn,
    clients,
    key,
    hasKey,
    email,
    name,
    isDelegatedUser,
    currentClient,
    client_contact_email,
    client_contact_name,
    setUser,
    fetchUser,
    fetchPendingShareRequests,
    requestClientDecryption,
    requestClientUserDecryption,
    fetchRolesAndPermissions,
    fetchDecryptedUser,
    insiderListUserInfos,
    fetchInsiderListUserInfosInBackground,
    logout,
    setInitialized,
    setAuthBootstrapping,
    testMasterPassword,
    clearMasterPasswordReentryAfterCreate,
    createMasterKey,
    editMasterKey,
    switchUser,
    updateUserInfo,
    getActiveClientLastChangeDate,
    fetchStatements,
    increaseLoginTime,
    unsecureLogin,
    passwordlessLogin,
    updateIncreaseLoginTime,
    toggleUnsecureLogin,
    togglePasswordlessLogin,
    removeMasterKey,
    isClientContact,
    statements,
    getRepresentativesAndBoardMembers,
    showEmailModal,
    isThisMyEmail,
    permanentDeleteUser,
  };
});
